import React, { useState, useEffect, useContext, useCallback } from 'react';
import Swal from 'sweetalert2';
import apiClient, { get, post } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import { formatMoney, formatDate, today, daysAgo, primero, useLocalTable } from '../finanzas-cxp/shared.js';
import FiltroFechas from '../finanzas-cxp/FiltroFechas.jsx';
import { buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * Bonificaciones a Proveedores -- réplica de `ListadoBonificacionesProveedores.jsx` +
 * `BonificacionesProveedoresCreate.jsx` (V1). El listado de V1 NO tiene botones por
 * fila (ni editar ni cancelar): solo Numero, Fecha, Proveedores, Observaciones,
 * Importe + fila TOTAL, filtros Fecha Inicio/Fin y Proveedor, PDF y Excel. El alta
 * (Fecha, Proveedor, Importe, Observaciones) vive aquí en un modal.
 */
const RECURSO = 'bonificaciones-proveedores';

function valoresIniciales() {
  return { fecha: today(), proveedores: '', importe: 0, observaciones: 'NA' };
}

function BonificacionForm({ open, onClose, proveedores, onSubmit }) {
  const [v, setV] = useState(valoresIniciales());
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (open) setV(valoresIniciales()); }, [open]);

  function set(k, val) { setV((s) => ({ ...s, [k]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!v.proveedores) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Debe seleccionar un proveedor' });
      return;
    }
    const r = await Swal.fire({
      title: 'Estas seguro?', text: 'Se registrará la bonificación!', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Guardar!',
    });
    if (!r.isConfirmed) return;
    setGuardando(true);
    try {
      await onSubmit(v);
      Swal.fire('Good job!', 'Creado con exito', 'success');
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: err.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Captura una Bonificacion" width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Regresar</button>
          <button type="submit" form="bonificacion-form" disabled={guardando} className="btn-primary">Registrar</button>
        </>
      )}
    >
      <form id="bonificacion-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required value={v.fecha} onChange={(e) => set('fecha', e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Proveedor</label>
          <select value={v.proveedores} onChange={(e) => set('proveedores', e.target.value)} className="select-base">
            <option value="">Selecciona</option>
            {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Importe</label>
          <input type="number" min="0" step="any" value={v.importe} onChange={(e) => set('importe', e.target.value)} className="input-base" />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Observaciones</label>
          <input type="text" required value={v.observaciones} onChange={(e) => set('observaciones', e.target.value)} className="input-base" />
        </div>
      </form>
    </Modal>
  );
}

export default function BonificacionesProveedoresPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso(`${RECURSO}:ver`);
  const puedeCrear = hasPermiso(`${RECURSO}:crear`);

  const [filas, setFilas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(daysAgo(30));
  const [fechaFin, setFechaFin] = useState(today());
  const [mailOpen, setMailOpen] = useState(false);
  const [filtroProveedor, setFiltroProveedor] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [data, provData] = await Promise.all([
        get(`/${RECURSO}/fechas/${fechaInicio}/${fechaFin}`),
        apiClient.get('/proveedores', { params: { limit: 500 } }).then((r) => r.data.data),
      ]);
      setProveedores(provData || []);
      setFilas((data || []).map((b) => {
        const prov = primero(b.proveedores);
        return {
          _id: b._id,
          numero: b.idBonificacionProveedor,
          fecha: b.fecha,
          proveedor: prov?.name || '—',
          _idProveedor: prov?._id,
          observaciones: b.observaciones || '',
          importe: b.importe,
        };
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { cargar(); }, [cargar]);

  async function guardar(v) {
    await post(`/${RECURSO}`, {
      fecha: v.fecha, importe: Number(v.importe), proveedores: v.proveedores, observaciones: v.observaciones,
    });
    await cargar();
  }

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(
    filas.filter((f) => !filtroProveedor || f._idProveedor === filtroProveedor),
    { searchFields: ['proveedor', 'observaciones', 'importe', 'numero'], defaultSortBy: 'numero', defaultSortOrder: 'desc', perPage: 50 },
  );

  const columnasPdf = [
    { key: 'numero', label: 'Numero' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'proveedor', label: 'Proveedor' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'importe', label: 'Importe', render: (r) => formatMoney(r.importe) },
  ];
  const columnasExcel = columnasPdf.map((c) => (c.key === 'importe' ? { key: 'importe', label: 'Importe' } : c));
  const totalExport = filtradas.reduce((a, r) => a + (r.importe || 0), 0);
  const armarPdf = () => buildTablePdf('Listado Abonos', columnasPdf, filtradas, { subtitulo: `${fechaInicio} - ${fechaFin}`, totalCols: { observaciones: 'Total', importe: `$${new Intl.NumberFormat('en-US').format(totalExport)}` } });

  const columnas = [
    { key: 'numero', label: 'Numero', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'proveedor', label: 'Proveedores', sortable: true },
    { key: 'observaciones', label: 'Observaciones', sortable: true },
    { key: 'importe', label: 'Importe', sortable: true, render: (r) => formatMoney(r.importe) },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Bonificaciones a Proveedores</h1>
        {puedeCrear && (
          <button onClick={() => setFormOpen(true)} className="btn-primary">Nueva Bonificacion</button>
        )}
      </div>

      <FiltroFechas inicio={fechaInicio} fin={fechaFin} onInicio={setFechaInicio} onFin={setFechaFin} />
      <div>
        <label className="label">Proveedores</label>
        <select value={filtroProveedor} onChange={(e) => { setFiltroProveedor(e.target.value); setPage(1); }} className="select-base w-64">
          <option value="">Selecciona</option>
          {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => (
            <option key={p._id} value={p._id}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="input-base w-64" />
        <button type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={async () => (await armarPdf()).save('Abonos.pdf')}>PDF</button>
        <button type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => setMailOpen(true)}>eMail</button>
        <button type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => exportTableExcel('ListadoBonificaciones', columnasExcel, filtradas, { fileName: 'ListadoBonificaciones', sheetName: 'ListadoBonificaciones' })}>Excel</button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ observaciones: 'TOTAL', importe: formatMoney(pageRows.reduce((a, r) => a + (r.importe || 0), 0)) }} />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Abonos"
        onEnviar={async (email) => {
          const doc = await armarPdf();
          await enviarCorreoAdjunto({
            subject: 'Listado Bonificaciones', email, fileName: 'ListadoBonificaciones.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras el listado de Bonificaciones.'),
          });
        }}
      />

      <BonificacionForm open={formOpen} onClose={() => setFormOpen(false)} proveedores={proveedores} onSubmit={guardar} />
    </div>
  );
}
